import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Shield, LayoutDashboard, Users, FolderKanban, Wallet, 
  Settings, FileText, LogOut, Menu, X, Bell, ChevronDown,
  UserCog, Activity
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLayout = ({ admin, token, onAdminLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!admin || !token) {
      navigate('/admin');
      return;
    }
    fetchStats();
  }, [admin, token]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard/stats?token=${token}`);
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/auth/logout?token=${token}`);
    } catch (error) {
      // Continue with logout even if API fails
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    onAdminLogout();
    navigate('/admin');
    toast.success('Logged out successfully');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/users', icon: Users, label: 'Users' },
    { path: '/admin/groups', icon: FolderKanban, label: 'Groups' },
    { path: '/admin/finance', icon: Wallet, label: 'Finance' },
    { path: '/admin/logs', icon: Activity, label: 'Activity Logs' },
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  if (admin?.role === 'super_admin') {
    navItems.push({ path: '/admin/admins', icon: UserCog, label: 'Admin Users' });
  }

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-40 h-screen transition-transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } md:translate-x-0`}>
        <div className="h-full w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
          {/* Logo */}
          <div className="p-5 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-white">SharePay</h1>
                <p className="text-xs text-slate-400">Admin Portal</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-red-500/20 to-orange-500/20 text-orange-400 border border-orange-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Admin Info */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
                <span className="text-white font-bold">
                  {admin?.name?.charAt(0)?.toUpperCase() || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{admin?.name}</p>
                <p className="text-xs text-slate-400 truncate capitalize">{admin?.role?.replace('_', ' ')}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full rounded-xl border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="fixed top-4 left-4 z-50 md:hidden p-2 rounded-xl bg-slate-800 text-white"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Main content */}
      <div className="md:ml-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800">
          <div className="px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-bold text-white">
                {navItems.find(item => isActive(item.path))?.label || 'Dashboard'}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {stats && (
                <div className="hidden sm:flex items-center gap-4 text-sm">
                  <div className="px-3 py-1 rounded-full bg-slate-800 text-slate-300">
                    <span className="text-green-400 font-bold">{stats.users?.total || 0}</span> Users
                  </div>
                  <div className="px-3 py-1 rounded-full bg-slate-800 text-slate-300">
                    <span className="text-blue-400 font-bold">{stats.groups?.active || 0}</span> Active Groups
                  </div>
                </div>
              )}
              <button className="p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white relative">
                <Bell className="w-5 h-5" />
                {stats?.contributions?.pending > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                    {stats.contributions.pending}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet context={{ admin, token, stats, refreshStats: fetchStats }} />
        </main>
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
